/**
 * Compliance Report Email Draft Builder
 *
 * Builds the plain-text subject + body a user copies into their own mail client when sending an
 * FAA or State compliance report to an agency.
 *
 * ⚠️ Plain text, NOT HTML. This draft is pasted into an arbitrary client; HTML pasted into a
 * plain-text composer arrives as literal tags.
 *
 * Pure function — no Angular injection, no I/O, no `Date.now()`. Everything it needs arrives as
 * an argument so it stays trivially testable.
 *
 * Reference: /agent-os/specs/2026-08-03-report-agency-email-stopgap/spec.md § 4 – § 4.7
 */

import { FaaReportOptions } from '../../core/data-models/faa-report-options.model';
import { StateReportOptions } from '../../core/data-models/state-report-options.model';
import { US_STATES } from '../constants/us-states';

// ============================================
// TYPES
// ============================================

/** Which template variant to build. Matches the shared email modal's `reportKind` input. */
export type ReportEmailKind = 'faa' | 'state';

/** Minimum shape the builder needs from an FAA report. `FaaReport` satisfies this. */
export interface FaaReportEmailSource {
  startDate: string;
  endDate: string;
  options: FaaReportOptions;
}

/** Minimum shape the builder needs from a state report. `StateReport` satisfies this. */
export interface StateReportEmailSource {
  /** 2-letter state code. */
  state: string;
  startDate: string;
  endDate: string;
  options: StateReportOptions;
}

/** Minimum shape the builder needs from an organization. `Organization` satisfies this. */
export interface ReportEmailOrganization {
  name: string;
}

export interface BuildReportEmailTemplateParams {
  report: FaaReportEmailSource | StateReportEmailSource;
  org: ReportEmailOrganization;
  /**
   * The exemption / applicator license number, already resolved (see `report-certificates.ts`).
   * Callers must pass a visible bracketed placeholder rather than an empty string when unknown.
   */
  certificateNumber: string;
  /**
   * Explicit variant selector. Optional — when omitted the kind is inferred from the report's
   * option shape, which is unambiguous because `FaaReportOptions` and `StateReportOptions` share
   * no keys.
   */
  kind?: ReportEmailKind;
}

export interface ReportEmailTemplate {
  subject: string;
  body: string;
}

// ============================================
// CONSTANTS
// ============================================

/**
 * Rendered ABOVE the draft on state reports only — deliberately NOT part of the body, because
 * it is guidance for the sender, not something an agency should receive.
 *
 * Filing requirements genuinely differ state to state, so the state template is a starting point
 * the user is expected to adjust.
 */
export const STATE_REPORT_REVIEW_NOTE =
  'Filing requirements vary by state — review this message and adjust it to your state\'s requirements before sending.';

/** Used when an organization somehow has no name; never emit an empty string into a filing. */
export const ORGANIZATION_NAME_PLACEHOLDER = '[enter organization name]';

/** Indent for bullet rows in the plain-text body. */
const BULLET_PREFIX = '  - ';

/** Column at which values line up in the state report's Operator / license / period block. */
const STATE_LABEL_WIDTH = 27;

// ============================================
// BULLET MAPS
// ============================================
//
// ⚠️ The two maps below CANNOT be shared or generalized. `FaaReportOptions` and
// `StateReportOptions` are completely different shapes describing completely different report
// sections. Every bullet must correspond to a section the attached PDF actually contains —
// claiming content the report does not include is a misstatement about a compliance submission.

/**
 * FAA bullets, in the order they appear in the report.
 * Source of truth for the option names: `FaaReportOptions`.
 */
const FAA_BULLETS: ReadonlyArray<readonly [keyof FaaReportOptions, string]> = [
  ['includeFlightLogs', 'Summary of all flight operations'],
  ['includeSprayRecords', 'Aerial application (spray) records'],
  ['includePilotInfo', 'Pilot information and certifications'],
  ['includeAircraftInfo', 'Aircraft information']
];

/**
 * State bullets, in the order they appear in the report.
 * Source of truth for the option names: `StateReportOptions`.
 *
 * `includeFieldMaps` renders as coordinates, not imagery — the state-reports spec deliberately
 * excluded map visualization in favor of lat/long text, so the bullet says "coordinates".
 */
const STATE_BULLETS: ReadonlyArray<readonly [keyof StateReportOptions, string]> = [
  ['includeChemicalDetails', 'Chemical and product details'],
  ['includeApplicationRates', 'Application rates'],
  ['includeFieldMaps', 'Field locations and coordinates'],
  ['includeWeatherConditions', 'Weather conditions at time of application']
];

// ============================================
// PUBLIC API
// ============================================

/**
 * Build the plain-text email draft for a compliance report.
 *
 * The bullet list is always derived from `report.options` so the message describes exactly what
 * the attached PDF contains. Unchecking an option on the generate form correctly changes the
 * email.
 */
export function buildReportEmailTemplate(
  params: BuildReportEmailTemplateParams
): ReportEmailTemplate {
  const kind = params.kind ?? inferReportKind(params.report);

  return kind === 'state'
    ? buildStateTemplate(
        params.report as StateReportEmailSource,
        params.org,
        params.certificateNumber
      )
    : buildFaaTemplate(params.report as FaaReportEmailSource, params.org, params.certificateNumber);
}

/**
 * Bullets an FAA report's options resolve to, in report order.
 * Exported for the modal's preview/tests; the builder uses it internally.
 */
export function buildFaaBullets(options: FaaReportOptions | null | undefined): string[] {
  return pickBullets(FAA_BULLETS, options);
}

/**
 * Bullets a state report's options resolve to, in report order.
 * Independent code path from {@link buildFaaBullets} — see the note on the bullet maps.
 */
export function buildStateBullets(options: StateReportOptions | null | undefined): string[] {
  return pickBullets(STATE_BULLETS, options);
}

// ============================================
// VARIANTS
// ============================================

function buildFaaTemplate(
  report: FaaReportEmailSource,
  org: ReportEmailOrganization,
  certificateNumber: string
): ReportEmailTemplate {
  const orgName = resolveOrgName(org);
  const bullets = buildFaaBullets(report.options);

  const subject = `FAA Report — ${orgName} — ${formatSubjectPeriod(report.startDate, report.endDate)}`;

  const body = joinSections([
    'Dear FAA UAS,',
    'Please find attached our monthly UAS operations report as required under\n' +
      `our exemption/authorization ${certificateNumber}.`,
    renderIncludesBlock(bullets),
    'All operations were conducted in accordance with our authorization\nlimitations and applicable regulations.',
    'If you require any additional information or clarification regarding this\nreport, please contact the pilot listed on the report directly, at the name\nand number shown there.',
    'Thank you for your continued support of our UAS program.',
    orgName
  ]);

  return { subject, body };
}

function buildStateTemplate(
  report: StateReportEmailSource,
  org: ReportEmailOrganization,
  certificateNumber: string
): ReportEmailTemplate {
  const orgName = resolveOrgName(org);
  const bullets = buildStateBullets(report.options);
  const stateName = resolveStateName(report.state);

  const subject = `${stateName} Pesticide Application Report — ${orgName} — ${formatSubjectPeriod(
    report.startDate,
    report.endDate
  )}`;

  const detailBlock = [
    `${'Operator:'.padEnd(STATE_LABEL_WIDTH)}${orgName}`,
    `${'Applicator license no.:'.padEnd(STATE_LABEL_WIDTH)}${certificateNumber}`,
    `${'Reporting period:'.padEnd(STATE_LABEL_WIDTH)}${formatShortDate(
      report.startDate
    )} – ${formatShortDate(report.endDate)}`
  ].join('\n');

  const body = joinSections([
    'To Whom It May Concern,',
    'Please find attached our aerial pesticide application report for\n' +
      `${orgName}, covering ${formatLongDate(report.startDate)} through ${formatLongDate(
        report.endDate
      )}.`,
    detailBlock,
    renderIncludesBlock(bullets),
    'All applications were made in accordance with product labeling, our\napplicator license, and applicable state regulations. Individual\napplicator license numbers for each pilot are listed in the report.',
    'If you require any additional information or clarification regarding this\nreport, please contact the pilot listed on the report directly, at the\nname and number shown there.',
    `Thank you,\n${orgName}`
  ]);

  return { subject, body };
}

// ============================================
// INTERNALS
// ============================================

/**
 * Infer the variant from the option shape. The two option interfaces share no keys, so this is
 * unambiguous; callers can still pass `kind` explicitly.
 */
function inferReportKind(report: FaaReportEmailSource | StateReportEmailSource): ReportEmailKind {
  const options = (report?.options ?? {}) as unknown as Record<string, unknown>;

  if (FAA_BULLETS.some(([key]) => key in options)) {
    return 'faa';
  }
  if (STATE_BULLETS.some(([key]) => key in options)) {
    return 'state';
  }

  // No usable options: fall back to the presence of a state code, which only state reports carry.
  return typeof (report as StateReportEmailSource)?.state === 'string' &&
    (report as StateReportEmailSource).state.trim().length > 0
    ? 'state'
    : 'faa';
}

function pickBullets<T extends object>(
  map: ReadonlyArray<readonly [keyof T, string]>,
  options: T | null | undefined
): string[] {
  if (!options) {
    return [];
  }
  return map.filter(([key]) => options[key] === true).map(([, label]) => label);
}

/**
 * The "This report includes:" block, or an empty string when no options are selected — an empty
 * bullet list under that heading would read as a claim about nothing.
 */
function renderIncludesBlock(bullets: string[]): string {
  if (bullets.length === 0) {
    return '';
  }
  return ['This report includes:', '', ...bullets.map((bullet) => `${BULLET_PREFIX}${bullet}`)].join(
    '\n'
  );
}

/** Join non-empty sections with a blank line between them. */
function joinSections(sections: string[]): string {
  return sections.filter((section) => section.trim().length > 0).join('\n\n');
}

function resolveOrgName(org: ReportEmailOrganization | null | undefined): string {
  const name = org?.name?.trim();
  return name && name.length > 0 ? name : ORGANIZATION_NAME_PLACEHOLDER;
}

/** Full state name for the subject line; falls back to the raw code when unrecognized. */
function resolveStateName(state: string | null | undefined): string {
  const code = (state ?? '').trim().toUpperCase();
  if (!code) {
    return 'State';
  }
  const match = US_STATES.find((entry) => entry.value === code);
  return match ? match.label : code;
}

// --------------------------------------------
// Dates
// --------------------------------------------

/**
 * Parse a report date.
 *
 * `YYYY-MM-DD` is parsed as a LOCAL date on purpose — `new Date('2026-01-11')` is UTC midnight,
 * which renders as Jan 10 for anyone west of UTC and would put the wrong reporting period on a
 * compliance filing.
 */
function parseReportDate(value: string | null | undefined): Date | null {
  if (!value) {
    return null;
  }

  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  const parsed = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** `Jan 11, 2026` — falls back to the raw input when unparseable. */
function formatShortDate(value: string | null | undefined): string {
  const date = parseReportDate(value);
  if (!date) {
    return (value ?? '').trim();
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** `January 11, 2026` — falls back to the raw input when unparseable. */
function formatLongDate(value: string | null | undefined): string {
  const date = parseReportDate(value);
  if (!date) {
    return (value ?? '').trim();
  }
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

/**
 * `Jan 11 – Feb 10, 2026` when both ends share a year, otherwise
 * `Dec 11, 2025 – Feb 10, 2026`. Agency inboxes are triaged by subject line, so the period has
 * to be readable at a glance.
 */
function formatSubjectPeriod(
  startDate: string | null | undefined,
  endDate: string | null | undefined
): string {
  const start = parseReportDate(startDate);
  const end = parseReportDate(endDate);

  if (!start || !end) {
    return [formatShortDate(startDate), formatShortDate(endDate)].filter(Boolean).join(' – ');
  }

  if (start.getFullYear() === end.getFullYear()) {
    const startLabel = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${startLabel} – ${formatShortDate(endDate)}`;
  }

  return `${formatShortDate(startDate)} – ${formatShortDate(endDate)}`;
}
